import React, {useState} from 'react';
import {useFormik} from 'formik';
import * as Yup from 'yup';
import {registrationRequest, trackRegistration} from './registrationRequest';
import positionData from '../../data/position.json';
import LoadingButton from "../LoadingButton";
import {logEvent} from "../../utils/analytics";
import * as fbq from '../../utils/FacebookPixel';
import { USER_API_URL } from "../../constants";

interface Step2Props {
    nextStep: () => void;
    prevStep: () => void;
    handleTeacherData: (data: { userId: string; role: string, name: string, surname: string, password: string, email: string, phone: string, positionId: number }) => void;
}

const Step2: React.FC<Step2Props> = ({ nextStep, handleTeacherData }) => {
    const [requestError, setRequestError] = useState('');
    const formik = useFormik({
        initialValues: {
            firstName: '', lastName: '', email: '', password: '', phone: '',
            positionId: positionData.positions[0].id.toString(),
        },
        validationSchema: Yup.object({
            firstName: Yup.string().trim().required('İsim zorunludur.'),
            lastName: Yup.string().trim().required('Soyisim zorunludur.'),
            email: Yup.string().trim().email('Geçerli bir e-posta adresi giriniz.')
                .max(50, 'E-posta en fazla 50 karakter olabilir.').required('E-posta zorunludur.'),
            password: Yup.string().required('Şifre zorunludur.')
                .min(3, 'Şifre en az 3 karakter olmalıdır.').max(50, 'Şifre en fazla 50 karakter olabilir.')
                .test('not-blank', 'Şifre yalnızca boşluklardan oluşamaz.', value => !value || !!value.trim()),
            phone: Yup.string().matches(/^[2-5][0-9]{9}$/, {
                message: 'Geçerli bir Türkiye telefon numarası giriniz (örn: 5xx xxx xx xx)',
                excludeEmptyString: true,
            }),
            positionId: Yup.string().oneOf(positionData.positions.map(position => String(position.id))).required(),
        }),
        onSubmit: async (values) => {
            setRequestError('');
            const formData = {...values, firstName: values.firstName.trim(),
                lastName: values.lastName.trim(), email: values.email.trim()};
            try {
                const payload = {
                    email: formData.email,
                    dateOfBirth: "1988",
                    password: formData.password,
                    marketingPermission: true,
                    weeklyProgressReport: true,
                    subUser: {
                        dateOfBirth: "2019-03-31T21:00:00.000Z",
                        name: formData.firstName,
                        avatarId: 26,
                    },
                    device: {
                        appVersion: "4.3.8",
                        appLanguage: "tr-TR",
                        regionCode: "TR",
                        storeRegionCode: "TUR",
                        storeRegionCurrency: "$",
                        timeZone: "Europe/Istanbul",
                        deviceResolution: "1920x1080",
                        deviceBrand: "Apple",
                        deviceModel: "Iphone 11",
                        deviceTypeId: 1,
                        operationSystem: "IOS",
                        pushToken: "randomToken",
                        pushTokenTypeId: 1,
                    },
                };

                const data = await registrationRequest(`${USER_API_URL}/main-user/register-teacher`, payload,
                    'Hesabınız oluşturulamadı. Bilgilerinizi kontrol edip tekrar deneyin.',
                    'Bu e-posta adresi zaten kullanılıyor. Lütfen farklı bir e-posta adresi deneyin.');

                handleTeacherData({
                    userId: data.data.id,
                    role: formData.positionId,
                    positionId: parseInt(formData.positionId),
                    name: formData.firstName,
                    surname: formData.lastName,
                    email: formData.email,
                    phone: formData.phone,
                    password: formData.password
                });
                trackRegistration(() => {
                    fbq.event('CreateTeacher', {
                        content_name: 'Teacher Registration',
                        content_category: 'Onboarding',
                        position: formData.positionId
                    });
                    logEvent("Button", "Clicked", "Create Teacher");

                    window.lintrk?.('track', { conversion_id: 21977089 });
                });

                nextStep();
            } catch (error) {
                setRequestError((error as Error).message);
            }
        },
    });
    const formData = formik.values;
    const fieldError = (field: keyof typeof formData) => formik.touched[field] && formik.errors[field]
        ? <div className="error">{formik.errors[field]}</div> : null;
    const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setRequestError('');
        if (event.target.name === 'phone') {
            formik.setFieldValue('phone', event.target.value.replace(/\D/g, ''));
        } else {
            formik.handleChange(event);
        }
    };

    return (
        <div className="form-container">
            <h2 className="mb-8">Kişisel Bilgilerinizi Girin</h2>
            <p className="mb-4"><span style={{color: 'red'}}>*</span> ile işaretli alanlar zorunludur.</p>
            <div className="row">
                <div className="col col-6">
                    <label>
                        İsim <span style={{color: 'red'}}>*</span>
                        <input
                            type="text"
                            name="firstName"
                            autoComplete="given-name"
                            value={formData.firstName}
                            onChange={handleChange}
                            onBlur={formik.handleBlur}
                        />
                        {fieldError('firstName')}
                    </label>
                </div>
                <div className="col col-6">
                    <label>
                        Soyisim <span style={{color: 'red'}}>*</span>
                        <input
                            type="text"
                            name="lastName"
                            autoComplete="family-name"
                            value={formData.lastName}
                            onChange={handleChange}
                            onBlur={formik.handleBlur}
                        />
                        {fieldError('lastName')}
                    </label>
                </div>
            </div>
            <label>
                E-posta <span style={{color: 'red'}}>*</span>
                <input
                    type="email"
                    name="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleChange}
                            onBlur={formik.handleBlur}
                />
                {fieldError('email')}
            </label>
            <label>
                <span className="optionalText">Telefon</span>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{
                        padding: '20px 23px',
                        backgroundColor: '#f5f5f5',
                        border: '1px solid #ddd',
                        borderRight: 'none',
                        borderRadius: '4px 0 0 4px',
                        fontSize: '16px'
                    }}>
                        +90
                    </span>
                    <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                            onBlur={formik.handleBlur}
                        placeholder="5xx xxx xx xx"
                        maxLength={10}
                        style={{
                            borderRadius: '0 4px 4px 0',
                            borderLeft: 'none',
                            flex: 1
                        }}
                    />
                </div>
                {fieldError('phone')}
            </label>
            <label>
                Şifre <span style={{color: 'red'}}>*</span>
                <input
                    type="password"
                    name="password"
                    autoComplete="new-password"
                    value={formData.password}
                    onChange={handleChange}
                            onBlur={formik.handleBlur}
                />
                {fieldError('password')}
            </label>

            <label>
                Öğretmen Rolü <span style={{color: 'red'}}>*</span>
                <select
                    id="positionId"
                    name="positionId"
                    value={formData.positionId}
                    onChange={handleChange}
                            onBlur={formik.handleBlur}
                >
                    {positionData.positions.map((position) => (
                        <option key={position.id} value={position.id}>
                            {position.name}
                        </option>
                    ))}
                </select>
            </label>
            {fieldError('positionId')}
            {requestError && <div className="alert alert-danger" role="alert">{requestError}</div>}
            <LoadingButton onClick={async () => {await formik.submitForm()}}>
                Sonraki adım
            </LoadingButton>
        </div>
    );
};

export default Step2;
